import React, { useMemo, useState } from "react";
import { Check, CheckCheck, Loader2, Receipt } from "lucide-react";
import PedidoOriginalModal from "../../../components/PedidoOriginalModal";
import {
  FilaBebidaDescricao,
  FilaBebidaItem,
} from "../../../services/filaBebidasService";
import { resolveSalaoMesaCliente } from "../../Painel/utils/origemPainel";
import {
  formatBebidaNomeComQuantidade,
  FilaBebidaItemAgrupado,
} from "../utils/agruparItensFilaBebidas";
import { BebidaMesaTicket as BebidaMesaTicketType } from "../utils/agruparBebidasPorMesa";

interface BebidaMesaTicketProps {
  ticket: BebidaMesaTicketType;
  busyIds: Set<number>;
  showFinalizar: boolean;
  onFinalizar: (ids: number[]) => void;
}

const resolveDescricao = (item: FilaBebidaItem): FilaBebidaDescricao | null => {
  if (Array.isArray(item.descricao) && item.descricao.length > 0) {
    return item.descricao[0];
  }
  return null;
};

function linhaBusy(linha: FilaBebidaItemAgrupado, busyIds: Set<number>): boolean {
  return linha.itemIds.some((id) => busyIds.has(id));
}

/** Nome do responsável da comanda (não o rótulo da mesa). */
function resolveNomeResponsavel(
  linha: FilaBebidaItemAgrupado,
): string | null {
  const { cliente } = resolveSalaoMesaCliente(
    linha.pedidoFoodfly?.mesaId,
    linha.pedidoFoodfly?.identificador,
  );
  return cliente;
}

const BebidaMesaTicket: React.FC<BebidaMesaTicketProps> = ({
  ticket,
  busyIds,
  showFinalizar,
  onFinalizar,
}) => {
  const [showPedidoModal, setShowPedidoModal] = useState(false);
  const ticketBusy = ticket.allItemIds.some((id) => busyIds.has(id));

  const tituloPrincipal =
    ticket.labels.mesa ||
    ticket.labels.comandaLabel ||
    ticket.labels.badge ||
    (ticket.linhas[0]?.numeroPedido != null
      ? `Pedido #${ticket.linhas[0].numeroPedido}`
      : "Pedido");

  const pedidoOriginalOrderId = useMemo(() => {
    for (const linha of ticket.linhas) {
      const n = parseInt(String(linha.foodflyPedidoId || ""), 10);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return null;
  }, [ticket.linhas]);

  return (
    <article className="flex min-h-0 flex-col overflow-hidden rounded-xl border-2 border-amber-300 bg-white shadow-md">
      <header className="shrink-0 space-y-2.5 border-b border-amber-200 bg-amber-50 px-3 py-3 sm:px-4 sm:py-3.5">
        {/* 1ª linha — identificação da mesa bem grande */}
        <div className="min-w-0">
          <h2 className="truncate text-2xl font-extrabold uppercase leading-none tracking-tight text-amber-950 sm:text-3xl">
            {tituloPrincipal}
          </h2>
          <p className="mt-1 text-xs font-semibold text-amber-900/70">
            {ticket.linhas.length}{" "}
            {ticket.linhas.length === 1 ? "bebida" : "bebidas"} ·{" "}
            {ticket.allItemIds.length} un.
          </p>
        </div>

        {/* 2ª linha — ações exclusivas */}
        <div className="flex w-full gap-2">
          {pedidoOriginalOrderId != null ? (
            <button
              type="button"
              onClick={() => setShowPedidoModal(true)}
              className="flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-lg border border-violet-600 bg-violet-600 px-3 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-violet-700"
              title="Ver recibo original"
              aria-label="Ver recibo original do pedido"
            >
              <Receipt size={16} strokeWidth={2.5} />
              Recibo
            </button>
          ) : null}
          {showFinalizar ? (
            <button
              type="button"
              onClick={() => onFinalizar(ticket.allItemIds)}
              disabled={ticketBusy || ticket.allItemIds.length === 0}
              className="flex min-h-[44px] flex-[1.4] items-center justify-center gap-1.5 rounded-lg border border-emerald-700 bg-emerald-700 px-3 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {ticketBusy ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <CheckCheck size={16} strokeWidth={2.5} />
              )}
              Liberar tudo
            </button>
          ) : null}
        </div>
      </header>

      <ul className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2.5 sm:p-3">
        {ticket.linhas.map((linha) => {
          const desc = resolveDescricao(linha);
          const nome = formatBebidaNomeComQuantidade(
            desc?.name || linha.produtoNome,
            linha.quantidade,
          );
          const responsavel = resolveNomeResponsavel(linha);
          const adicionais = desc?.adicionais?.length
            ? desc.adicionais
            : Array.isArray(linha.adicionais)
              ? linha.adicionais.map((a) =>
                  a.quantidade && a.quantidade > 1
                    ? `${a.nome} x${a.quantidade}`
                    : a.nome,
                )
              : [];
          const observacao = desc?.observacao || linha.observacao || null;
          const busy = linhaBusy(linha, busyIds);
          const canFinalizar =
            showFinalizar &&
            (linha.status === "pending" || linha.status === "in_progress");

          return (
            <li
              key={String(Math.min(...linha.itemIds))}
              className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 sm:p-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-base font-bold leading-5 text-slate-900 sm:text-lg">
                    {nome}
                  </p>
                  {responsavel ? (
                    <p className="mt-0.5 truncate text-xs font-medium text-neutral-500">
                      {responsavel}
                    </p>
                  ) : null}
                  {adicionais.length > 0 ? (
                    <div className="mt-1 space-y-0.5">
                      {adicionais.map((a, idx) => (
                        <p
                          key={`${linha.id}-ad-${idx}`}
                          className="text-sm font-semibold text-slate-700"
                        >
                          {a}
                        </p>
                      ))}
                    </div>
                  ) : null}
                  {observacao ? (
                    <p className="mt-1.5 rounded-md border border-error-dark bg-error-light px-2 py-1 text-xs font-bold text-error-text">
                      {observacao}
                    </p>
                  ) : null}
                </div>
                {canFinalizar ? (
                  <button
                    type="button"
                    onClick={() => onFinalizar(linha.itemIds)}
                    disabled={busy}
                    className="flex min-h-[44px] shrink-0 items-center justify-center gap-1 rounded-lg border border-emerald-600 bg-emerald-600 px-3 py-2 text-sm font-bold text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0 sm:py-1.5 sm:text-xs"
                  >
                    {busy ? (
                      <Loader2 size={15} className="animate-spin" />
                    ) : (
                      <Check size={15} strokeWidth={2.5} />
                    )}
                    Finalizar
                  </button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>

      {pedidoOriginalOrderId != null ? (
        <PedidoOriginalModal
          orderId={pedidoOriginalOrderId}
          isOpen={showPedidoModal}
          onClose={() => setShowPedidoModal(false)}
        />
      ) : null}
    </article>
  );
};

export default BebidaMesaTicket;
